import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Trophy, FlaskConical, Code, Cog, Calculator, Bot,
  Tag, CheckCircle2, Sunrise, Sunset, Calendar, Clock,
} from 'lucide-react';

const STEM_PILLARS = [
  { label: 'Science', icon: FlaskConical, color: 'from-rose-500 to-pink-400' },
  { label: 'Technology', icon: Code, color: 'from-blue-500 to-indigo-400' },
  { label: 'Engineering', icon: Cog, color: 'from-emerald-500 to-teal-400' },
  { label: 'Math', icon: Calculator, color: 'from-purple-500 to-violet-400' },
  { label: 'Robotics', icon: Bot, color: 'from-orange-500 to-amber-400' },
];

/**
 * Hero for /summer-camps.
 * Right panel showcases All-Inclusive Pricing with single-session flexibility
 * (parents can book a single AM or PM half-day session instead of a full week).
 */
export default function CampExploreHero() {
  const navigate = useNavigate();

  const scrollToCamps = () => {
    const el = document.getElementById('browse-camps');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative overflow-hidden bg-rsc-navy">
      {/* Background photograph — Cloudinary hero shot (May 21, 2026 user request).
          Sits behind the dark overlay so the foreground copy stays legible. The
          original animated gradient + glow blobs are layered ON TOP via
          rgba/blur so we keep the brand sheen without losing the photo. */}
      <img
        src="https://res.cloudinary.com/dfa8raimq/image/upload/v1779395786/18_2_jnytlr.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        data-testid="summer-camps-hero-bg"
      />
      {/* Dark gradient overlay — restores the rsc-navy mood + readability. */}
      <div className="absolute inset-0 bg-gradient-to-br from-rsc-navy/85 via-[#1a3a6d]/75 to-[#0f2847]/85 pointer-events-none" />
      {/* Subtle yellow grid (kept from original design — adds that branded sheen) */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />
      <div className="absolute top-20 -right-40 w-[600px] h-[600px] bg-orange-500/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-10 -left-40 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Announcement banner */}
      <div className="bg-rsc-yellow text-rsc-navy py-2 px-4 text-center relative z-10">
        <p className="text-sm font-bold">
          Summer 2026 Camps Now Open — Limited Spots Available
        </p>
      </div>

      <div className="container mx-auto px-4 py-8 lg:py-10 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
          {/* Left — condensed hero content */}
          <div className="text-white space-y-5">
            <div className="space-y-3">
              <div className="inline-block px-3 py-1.5 bg-rsc-yellow/20 backdrop-blur-sm rounded-full text-xs font-bold border border-rsc-yellow/30">
                <Trophy className="inline w-3.5 h-3.5 mr-1.5 text-rsc-yellow" />
                <span className="text-rsc-yellow">Top 5% STEM.org Accredited</span>
              </div>

              <h1 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight">
                5 STEM Pillars.<br />
                <span className="text-rsc-yellow">One Epic Summer.</span>
              </h1>

              <p className="text-base text-white/80">
                Hands-on STEM summer camps for grades K–8 — book a single morning, a single afternoon, or the whole week.
              </p>
            </div>

            {/* Compact pillar row */}
            <div className="flex flex-wrap gap-2" data-testid="stem-pillars">
              {STEM_PILLARS.map((pillar) => {
                const Icon = pillar.icon;
                return (
                  <div key={pillar.label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full pl-2 pr-4 py-1.5 border border-white/10">
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${pillar.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-white">{pillar.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Stats row */}
            <div className="flex gap-6 pt-1">
              <div>
                <div className="text-2xl font-bold text-yellow-300">8 Weeks</div>
                <div className="text-xs text-white/70">June – August</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-300">40</div>
                <div className="text-xs text-white/70">Course Topics</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-300">4</div>
                <div className="text-xs text-white/70">Locations</div>
              </div>
            </div>

            {/* Jump button */}
            <div className="flex flex-wrap gap-3 pt-1">
              <button
                type="button"
                onClick={scrollToCamps}
                className="inline-flex items-center gap-2 bg-rsc-yellow text-rsc-navy font-bold rounded-full px-5 py-2 text-sm shadow-lg hover:bg-yellow-400 transition"
                data-testid="hero-browse-camps-btn"
              >
                Browse All Camps ↓
              </button>
            </div>
          </div>

          {/* Right — All-Inclusive Pricing panel (replaces lead form) */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-7 max-w-md mx-auto w-full" data-testid="hero-pricing-panel">
            <div className="text-center mb-5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-[11px] font-bold mb-2">
                <Tag className="w-3 h-3" /> ALL-INCLUSIVE PRICING
              </div>
              <h2 className="text-xl lg:text-2xl font-bold text-rsc-navy leading-tight">
                STEM kit included.<br />
                <span className="text-orange-600">No hidden fees.</span>
              </h2>
            </div>

            {/* Featured card — Full-Day Camp (BEST VALUE) */}
            <div className="rounded-xl bg-gradient-to-br from-emerald-50 via-yellow-50 to-amber-50 border-2 border-emerald-500 p-5 mb-3 relative shadow-md" data-testid="price-fullday-featured">
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-3 py-0.5 rounded-full text-[10px] font-extrabold tracking-wide shadow flex items-center gap-1">
                <Trophy className="w-3 h-3" /> BEST VALUE · 35% OFF
              </div>
              <div className="text-center mt-1">
                <div className="text-[11px] font-bold text-gray-500 tracking-wider">GRADES 3–8 · 5 DAYS</div>
                <div className="text-lg font-extrabold text-rsc-navy mt-0.5">Full-Week Camp</div>
                <div className="mt-2 flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-extrabold text-emerald-600">$45</span>
                  <span className="text-sm text-gray-600">/session</span>
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  <span className="line-through">$69</span> · save $24 per session
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-[11px]">
                  <div className="bg-white rounded-md px-2 py-1.5 border border-emerald-200">
                    <div className="font-bold text-rsc-navy">AM + PM</div>
                    <div className="text-gray-600">9am – 4pm · lunch 12–1pm</div>
                  </div>
                  <div className="bg-white rounded-md px-2 py-1.5 border border-emerald-200">
                    <div className="font-bold text-rsc-navy">$450/week</div>
                    <div className="text-gray-600">10 sessions</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary tier — Half-Day weekly */}
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 mb-2" data-testid="price-halfday-week">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-rsc-navy/60 flex-shrink-0" />
                    <div className="text-sm font-bold text-rsc-navy">Half-Day (Weekly)</div>
                    <span className="text-[9px] font-extrabold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">SAVE 29%</span>
                  </div>
                  <div className="text-[11px] text-gray-600 mt-0.5">5 days · AM or PM · $245/wk</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xl font-extrabold text-rsc-navy leading-none">
                    $49<span className="text-[10px] text-gray-500 font-normal">/session</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tertiary — Single Session (most flexible) */}
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 mb-3" data-testid="price-single-session">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Sunrise className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                    <div className="text-sm font-bold text-rsc-navy">Single AM/PM Session</div>
                    <span className="text-[9px] font-extrabold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap">MOST FLEXIBLE</span>
                  </div>
                  <div className="text-[11px] text-gray-600 mt-0.5">9am–12pm or 1pm–4pm · no commitment</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xl font-extrabold text-rsc-navy leading-none">
                    $69<span className="text-[10px] text-gray-500 font-normal">/session</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Extended care note */}
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-rsc-navy bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3" data-testid="extended-care-note">
              <Clock className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
              <span><strong>Extended care available</strong> — early drop-off &amp; late pick-up · $100/wk</span>
            </div>

            <Button
              type="button"
              onClick={scrollToCamps}
              className="w-full bg-gradient-to-r from-rsc-yellow to-amber-400 hover:from-rsc-yellow-bright hover:to-amber-500 text-rsc-navy font-bold py-5 text-base shadow-lg"
              data-testid="hero-register-cta-btn"
            >
              Browse Camps &amp; Register →
            </Button>

            <p className="text-[11px] text-center text-gray-500 mt-2">
              Early-bird &amp; multi-week discounts applied at checkout
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
